import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ToasterService } from '../../../services/core/toaster.service';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BackendApiService } from '../../../services/backend-api.service';
import { UserService } from '../../../services/core/user.service';
import { environment } from '../../../../environments/environment';
import { HttpEventType } from '@angular/common/http';
import { DocumentService } from '../../document/document.service';
import { AttachmentsServiceService } from '../../../services/attachments/attachments-service.service';
import { CurrentModuleContextService } from '../../../services/core/current-module-context.service';
import { ThumbnailGeneratorService } from '../../../services/attachments/thumbnail-generator.service';

interface UploadFile {
  file: File;
  preview: string | null;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  responseData?: any;
  attach_file?: any;
  attachment_id?: number;
  isFromEdit?: boolean; // Track if file loaded from edit mode
}

@Component({
    selector: 'app-attachment',
    templateUrl: './attachment.component.html',
    styleUrls: ['./attachment.component.scss'],
    standalone: false
})
export class AttachmentComponent implements OnInit {
  // ============================================================================
  // NEW PROPERTIES - File Queue Management
  // ============================================================================
  files: UploadFile[] = [];
  isDragging = false;
  // Number of files currently being dragged, for the overlay's count badge.
  // Only meaningful when `dragOverlay` is on.
  draggedFileCount = 0;
  isUploading = false;
  totalFiles = 0;
  uploadedFiles = 0;
  failedFiles = 0;

  // ============================================================================
  // EXISTING PROPERTIES - Maintain Backward Compatibility
  // ============================================================================
  @Input() uploadUrl: string = 'attachments';
  @Input() isRequired: boolean = false;
  @Input() model: any = {};
  @Input() module_id: any;
  @Input() fileTypes: any = ['.jpg', '.jpeg', '.gif', '.png'];
  @Input() multipleUpload: boolean = false;
  @Input() dropZoneId: any = 'defaultFilesDropZone';
  @Input() multipleImagesOnEdit: any = [];
  @Input() multipleUploadData: any = []; // Backward compatibility - same as multipleImagesOnEdit
  @Input() imageOnEdit: any = '';
  @Input() isWebView?: any = false;
  @Input() dropZoneView: boolean = true;
  @Input() additionalParameters: any = {};
  @Input() allowAllFileTypes: boolean = false;
  @Input() showAllowedFileExtensions: boolean = true;

  // Opt in to the app's standard drag & drop overlay — the orange dashed
  // backdrop with the white card, icon circle and file-count badge used by the
  // document manager and copied into Task / Note / Ticket attachments, Module
  // Documents and the Jobsite Gallery. Off by default so the ~55 existing
  // usages of this component keep the plain border-highlight they have today.
  @Input() dragOverlay: boolean = false;

  // NEW INPUT - Manual Upload Toggle (default: false for auto-upload)
  @Input() manualUpload: boolean = false;
  @Input() folderId: number | string = '';
  @Input() endpoint?: string; // optional custom endpoint

  @Output() callback = new EventEmitter<any>(true);
  @Output() onDelete = new EventEmitter<any>(true);

  public sectionForm: UntypedFormGroup = this.serviceForm();

  constructor(
    public toasterService: ToasterService,
    public attachmentsServiceService: AttachmentsServiceService,
    public backendServiceApi: BackendApiService,
    private currentUser: UserService,
    private documentService: DocumentService,
    private moduleContext: CurrentModuleContextService,
    private thumbnailGeneratorService: ThumbnailGeneratorService
  ) {}

  ngOnInit(): void {
    this.initFormControlValues(this.model);

    // Load existing files if in edit mode
    // Check multipleUploadData first (backward compatibility), then multipleImagesOnEdit
    const existingFiles =
      this.multipleUploadData?.length > 0
        ? this.multipleUploadData
        : this.multipleImagesOnEdit;

    if (existingFiles && existingFiles.length > 0) {
      this.loadExistingFiles(existingFiles);
    } else if (this.imageOnEdit) {
      this.loadExistingFile(this.imageOnEdit);
    }
  }

  // ============================================================================
  // FORM MANAGEMENT (Keep existing logic)
  // ============================================================================
  initFormControlValues(value: any) {
    if (value) {
      this.sectionForm = this.serviceForm(value);
    }
    return this.sectionForm;
  }

  serviceForm(value: any = null) {
    return new UntypedFormGroup({
      file: new UntypedFormControl(
        value ? value : null,
        this.isRequired ? [Validators.required] : []
      ),
      file_new: new UntypedFormControl(
        value ? value : null,
        this.isRequired ? [Validators.required] : []
      ),
    });
  }

  // ============================================================================
  // DRAG & DROP HANDLERS
  // ============================================================================
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    // dataTransfer.files is empty during dragover for security reasons; items
    // is the only thing that reports a count before the drop.
    const items = event.dataTransfer?.items;
    this.draggedFileCount = items
      ? Array.from(items).filter(i => i.kind === 'file').length
      : 0;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.draggedFileCount = 0;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.draggedFileCount = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(files);
    }
  }

  // ============================================================================
  // FILE SELECTION & VALIDATION
  // ============================================================================
  onFileSelect(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.addFiles(files);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  }

  private addFiles(fileList: FileList): void {
    // Clear existing files if not in multiple upload mode
    if (!this.multipleUpload) {
      this.files = [];
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      // Validate file type
      if (!this.isValidFileType(file)) {
        this.toasterService.showWarning(
          `${file.name} is not an allowed file type`
        );
        continue;
      }

      // Validate file size (1GB max)
      if (!this.isValidFileSize(file)) {
        this.toasterService.showWarning(`${file.name} is too large (max 1GB)`);
        continue;
      }

      const uploadFile: UploadFile = {
        file: file,
        preview: null,
        progress: 0,
        status: 'pending',
      };

      // Generate preview (images, videos, PDFs) via the shared thumbnail service
      this.generatePreview(uploadFile);

      this.files.push(uploadFile);

      // Auto-upload if manualUpload is false (default behavior)
      if (!this.manualUpload) {
        this.uploadSingleFileAuto(uploadFile);
      }
    }

    this.totalFiles = this.files.length;
  }

  private isValidFileType(file: File): boolean {
    if (this.allowAllFileTypes) {
      return true;
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    return this.fileTypes.includes(fileExtension);
  }

  private isValidFileSize(file: File): boolean {
    const maxFileSize = 1000000000; // 1GB
    return file.size <= maxFileSize;
  }

  private generatePreview(uploadFile: UploadFile): void {
    this.thumbnailGeneratorService.generateForFile(uploadFile.file).then((preview) => {
      uploadFile.preview = preview;
    });
  }

  // ============================================================================
  // FILE REMOVAL
  // ============================================================================
  removeFile(index: number): void {
    const file = this.files[index];

    // If file is already uploaded to server, delete it
    if (file.status === 'success' && file.attachment_id) {
      this.deleteAttachmentById(file.attachment_id, index);
    } else {
      // Just remove from queue
      this.files.splice(index, 1);
      this.totalFiles = this.files.length;
      this.emitCallback();
    }
  }

  clearAllFiles(): void {
    this.files = [];
    this.totalFiles = 0;
    this.uploadedFiles = 0;
    this.failedFiles = 0;
    this.emitCallback();
  }

  private deleteAttachmentById(id: number, index?: number): void {
    this.backendServiceApi.deleteApi(`attachments/${id}`).subscribe({
      next: (response: any) => {
        if (response?.data?.message) {
          this.toasterService.showSuccessMessage(response?.data.message);
        }

        // Remove from files array
        if (index !== undefined) {
          this.files.splice(index, 1);
          this.totalFiles = this.files.length;
        }

        this.onDelete.emit(true);
        this.emitCallback();
      },
      error: (error) => {
        this.toasterService.showErrorMessage('Failed to delete file');
      },
    });
  }

  // ============================================================================
  // AUTO UPLOAD (Single File)
  // ============================================================================
  private async uploadSingleFileAuto(uploadFile: UploadFile): Promise<void> {
    uploadFile.status = 'uploading';
    this.isUploading = true;

    try {
      await this.uploadSingleFile(uploadFile);
      uploadFile.status = 'success';
      uploadFile.progress = 100;
      this.uploadedFiles++;
      this.emitCallback();
    } catch (error: any) {
      uploadFile.status = 'error';
      uploadFile.error = error.message || 'Upload failed';
      this.failedFiles++;
      this.toasterService.showErrorMessage(
        `Failed to upload ${uploadFile.file.name}`
      );
    } finally {
      this.isUploading = false;
    }
  }

  // ============================================================================
  // MANUAL UPLOAD (All Files)
  // ============================================================================
  async uploadFiles(): Promise<void> {
    if (this.files.length === 0) {
      this.toasterService.showError('No files to upload');
      return;
    }

    this.isUploading = true;
    this.uploadedFiles = 0;
    this.failedFiles = 0;

    for (const uploadFile of this.files) {
      if (uploadFile.status === 'success') {
        continue; // Skip already uploaded files
      }

      uploadFile.status = 'uploading';

      try {
        await this.uploadSingleFile(uploadFile);
        uploadFile.status = 'success';
        uploadFile.progress = 100;
        this.uploadedFiles++;
      } catch (error: any) {
        uploadFile.status = 'error';
        uploadFile.error = error.message || 'Upload failed';
        this.failedFiles++;
      }
    }

    this.isUploading = false;
    this.emitCallback();

    if (this.failedFiles === 0) {
      this.toasterService.showSuccessMessage(
        `All ${this.uploadedFiles} files uploaded successfully`
      );
    } else {
      this.toasterService.showWarning(
        `${this.uploadedFiles} uploaded, ${this.failedFiles} failed`
      );
    }
  }

  // ============================================================================
  // CORE UPLOAD LOGIC
  // ============================================================================
  private uploadSingleFile(uploadFile: UploadFile): Promise<void> {
    return new Promise((resolve, reject) => {
      // Resolve module + record context: explicit inputs always win, else fall
      // back to the centralized CurrentModuleContext. Folder resolution and
      // payload names are unchanged.
      const context = this.moduleContext.getContext();
      const uploadParams = {
        folder_id: (this.folderId === '' ? '' : Number(this.folderId)) as any,
        attachment_field:
          this.additionalParameters?.attachment_field || 'image',
        ...this.additionalParameters,
        // module_id and attachable_id are resolved AFTER the spread so an
        // undefined value inside additionalParameters (typed `any`) can never
        // clobber the context fallback. Explicit inputs still win via the ?? .
        module_id: this.module_id ?? context.moduleId,
        attachable_id:
          this.additionalParameters?.attachable_id ?? context.referenceId,
      };

      // Use custom endpoint if provided, else use uploadUrl (default: 'attachments')
      const targetEndpoint = this.endpoint || this.uploadUrl;

      // Upload to the attachments endpoint (or custom endpoint if provided)
      const upload$ = this.documentService.uploadToEndpointWithProgress(
        targetEndpoint,
        uploadFile.file,
        uploadParams
      );

      upload$.subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            if (event.total) {
              const percentDone = Math.round(
                (100 * event.loaded) / event.total
              );
              uploadFile.progress = percentDone;
            }
          } else if (event.type === HttpEventType.Response) {
            uploadFile.progress = 100;
            const responseBody = event.body;

            // Store response data
            uploadFile.responseData = responseBody;

            if (responseBody && responseBody.data) {
              uploadFile.attach_file =
                responseBody.data.attach_file || responseBody.data;
              uploadFile.attachment_id =
                responseBody.data.attachment_id || responseBody.data.id;
            }

            resolve();
          }
        },
        error: (error: any) => {
          console.error('Upload error:', error);
          reject(error);
        },
      });
    });
  }

  // ============================================================================
  // CALLBACK EMISSION
  // ============================================================================
  private emitCallback(): void {
    const successfulFiles = this.files.filter((f) => f.status === 'success');

    if (this.multipleUpload) {
      const attachmentData = successfulFiles.map((f) => ({
        attach_file: f.attach_file,
        attachment_id: f.attachment_id,
      }));

      // Update multipleUploadData for two-way binding compatibility
      this.multipleUploadData = attachmentData;

      this.callback.emit({ attachment: attachmentData });
    } else if (successfulFiles.length > 0) {
      const file = successfulFiles[0];
      const attachmentData = {
        attach_file: file.attach_file,
        attachment_id: file.attachment_id,
      };
      this.callback.emit({
        attachment: attachmentData,
      });
    }
  }

  // ============================================================================
  // LOAD EXISTING FILES (Edit Mode)
  // ============================================================================
  private loadExistingFiles(files: any[]): void {
    files.forEach((fileData) => {
      const uploadFile: UploadFile = {
        file: new File([], fileData?.attach_file?.original_name || 'file'),
        preview: fileData?.attach_file?.attachment,
        progress: 100,
        status: 'success',
        attach_file: fileData?.attach_file,
        attachment_id: fileData?.attachment_id,
        isFromEdit: true, // Mark as loaded from edit mode
      };
      this.files.push(uploadFile);
    });
    this.totalFiles = this.files.length;
    this.uploadedFiles = this.files.length;

    // Emit callback to notify parent component about existing files
    // Use setTimeout to ensure Angular change detection cycle completes
    setTimeout(() => {
      this.emitCallback();
    }, 0);
  }

  private loadExistingFile(fileData: any): void {
    const uploadFile: UploadFile = {
      file: new File(
        [],
        fileData?.original_name ||
          fileData?.attachment?.split('/')?.pop() ||
          'file'
      ),
      preview: fileData?.url || fileData?.attachment,
      progress: 100,
      status: 'success',
      attach_file: fileData,
      attachment_id: fileData?.id,
      isFromEdit: true, // Mark as loaded from edit mode
    };
    this.files.push(uploadFile);
    this.totalFiles = 1;
    this.uploadedFiles = 1;

    // Emit callback to notify parent component about existing file
    // Use setTimeout to ensure Angular change detection cycle completes
    setTimeout(() => {
      this.emitCallback();
    }, 0);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getFileIcon(fileName: string): string {
    if (!fileName) return 'insert_drive_file';

    const ext = fileName.split('.').pop()?.toLowerCase();

    // Image files
    if (
      ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext || '')
    ) {
      return 'image';
    }

    // PDF files
    if (ext === 'pdf') {
      return 'picture_as_pdf';
    }

    // Document files
    if (['doc', 'docx'].includes(ext || '')) {
      return 'description';
    }

    // Spreadsheet files
    if (['xls', 'xlsx', 'csv'].includes(ext || '')) {
      return 'table_chart';
    }

    // Presentation files
    if (['ppt', 'pptx'].includes(ext || '')) {
      return 'slideshow';
    }

    // Archive files
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) {
      return 'folder_zip';
    }

    // Video files
    if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext || '')) {
      return 'videocam';
    }

    // Audio files
    if (['mp3', 'wav', 'ogg', 'flac'].includes(ext || '')) {
      return 'audiotrack';
    }

    // CAD files
    if (['dwg', 'dxf', 'dwf'].includes(ext || '')) {
      return 'architecture';
    }

    return 'insert_drive_file';
  }

  get supportedFilesText(): string {
    if (this.allowAllFileTypes) {
      return 'All file types supported';
    }
    return `Supported: ${this.fileTypes.join(', ')}`;
  }

  get acceptedFileTypes(): string {
    if (this.allowAllFileTypes) {
      return '*';
    }
    return this.fileTypes.join(',');
  }

  allFilesUploaded(): boolean {
    return this.files.every((f) => f.status === 'success');
  }

  /**
   * Check if any file is from edit mode (existing file)
   */
  hasExistingFile(): boolean {
    return this.files.some((f) => f.isFromEdit === true);
  }

  /**
   * Check if any file is successfully uploaded
   */
  hasSuccessfulFile(): boolean {
    return this.files.some((f) => f.status === 'success');
  }
}
